import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Building2Icon,
  CheckIcon,
  ChevronDownIcon,
  Loader2Icon,
  RefreshCwIcon,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";
import { cn } from "@/lib/utils";
import { selectWorkspaces } from "../../pages/login/selectors";
import { selectSession } from "../../session/selectors";
import { useLazyWorkspacesQuery } from "../../services/api";
import { setSession } from "../../session/actions";
import config from "../../config";

/*
 * Sidebar workspace selector. Hosted inside the (still-MUI) DashboardSidebar.
 * Renders skeleton while workspaces load, an error row with retry if the
 * fetch fails, and a dropdown to switch tenants when the user has more than
 * one. Switching is a Redux + page reload — the axios interceptor picks up
 * the new `X-Tenant-Id` from Redux state on the next request.
 */

interface Workspace {
  id: number | string;
  name: string;
  tenant: string;
  logo?: string;
}

function getInitials(name: string | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((word) => word[0])
    .filter(Boolean)
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function WorkspaceSelectorDropdown() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const workspaces = useSelector(selectWorkspaces()) as Workspace[] | null;
  const session = useSelector(selectSession()) as { tenant?: string } | null;

  const [switching, setSwitching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!workspaces);
  const [fetchError, setFetchError] = useState(false);
  const [imageError, setImageError] = useState(false);

  const currentWorkspace =
    workspaces?.find((ws) => ws.tenant === session?.tenant) ?? null;
  const hasMultipleWorkspaces = (workspaces?.length ?? 0) > 1;

  const [triggerWorkspaces] = useLazyWorkspacesQuery();

  function fetchWorkspaces() {
    setInitialLoading(true);
    setFetchError(false);
    triggerWorkspaces(undefined)
      .unwrap()
      .catch(() => {
        setFetchError(true);
        setInitialLoading(false);
      });
  }

  useEffect(() => {
    if (!workspaces) fetchWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (workspaces) {
      setInitialLoading(false);
      setFetchError(false);
    }
  }, [workspaces]);

  // Reset image error when workspace changes.
  useEffect(() => {
    setImageError(false);
  }, [currentWorkspace?.id]);

  async function handleWorkspaceChange(workspace: Workspace) {
    if (workspace.tenant === session?.tenant) return;

    // With Cognito the access token is tenant-agnostic — every authenticated
    // request is scoped by the X-Tenant-Id header. Switching tenants is just
    // a Redux update; the axios interceptor picks up the new value from Redux
    // state on the next request, and the page reload re-fetches under the new tenant.
    setSwitching(true);

    // Persist the preferred tenant so it's restored on next login.
    // Fire-and-forget — don't block the UI on this. Auth headers are
    // injected by the global axios interceptor in store.js.
    axios.post(`${config.apiBaseUrl}/su/auth/change-tenant`, {
      tenant: workspace.tenant,
    }).catch(() => {});

    dispatch(setSession({ ...session, tenant: workspace.tenant }));
    window.location.reload();
  }

  if (initialLoading) {
    return (
      <div className="px-3 py-3">
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <Skeleton className="h-5 w-32" />
        </div>
      </div>
    );
  }

  if (fetchError && !currentWorkspace) {
    return (
      <div className="px-3 py-3">
        <div className="flex items-center gap-3">
          <Avatar className="size-10">
            <AvatarFallback className="bg-primary text-primary-foreground">
              <Building2Icon className="size-4" />
            </AvatarFallback>
          </Avatar>
          <span className="flex-1 truncate text-xs text-muted-foreground">
            {t("common.api-error")}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={fetchWorkspaces}
            aria-label={t("common.retry")}
          >
            <RefreshCwIcon className="size-4" />
          </Button>
        </div>
      </div>
    );
  }

  const trigger = (
    <button
      type="button"
      disabled={switching || !hasMultipleWorkspaces}
      className={cn(
        "group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors",
        hasMultipleWorkspaces && "hover:bg-accent",
        !hasMultipleWorkspaces && "cursor-default"
      )}
    >
      {currentWorkspace?.logo && !imageError ? (
        <Avatar className="size-10">
          <AvatarImage
            src={currentWorkspace.logo}
            alt={currentWorkspace.name}
            onError={() => setImageError(true)}
          />
          <AvatarFallback className="bg-primary text-primary-foreground">
            {getInitials(currentWorkspace.name)}
          </AvatarFallback>
        </Avatar>
      ) : (
        <Avatar className="size-10">
          <AvatarFallback className="bg-primary text-primary-foreground">
            {currentWorkspace?.name ? (
              getInitials(currentWorkspace.name)
            ) : (
              <Building2Icon className="size-4" />
            )}
          </AvatarFallback>
        </Avatar>
      )}
      <span className="flex-1 truncate text-sm font-semibold">
        {currentWorkspace?.name ?? ""}
      </span>
      {switching ? (
        <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
      ) : hasMultipleWorkspaces ? (
        <ChevronDownIcon className="size-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
      ) : null}
    </button>
  );

  if (!hasMultipleWorkspaces) {
    return <div className="px-1 py-2">{trigger}</div>;
  }

  return (
    <div className="px-1 py-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={8}
          className="w-[260px] max-h-[400px] overflow-y-auto"
        >
          {workspaces?.map((workspace) => {
            const isCurrent = workspace.tenant === session?.tenant;
            return (
              <DropdownMenuItem
                key={workspace.id}
                onSelect={() => handleWorkspaceChange(workspace)}
                className={cn("gap-3 py-2", isCurrent && "bg-accent/50")}
              >
                <Avatar className="size-8">
                  {workspace.logo ? (
                    <AvatarImage
                      src={workspace.logo}
                      alt={workspace.name}
                    />
                  ) : null}
                  <AvatarFallback className="text-xs">
                    {getInitials(workspace.name)}
                  </AvatarFallback>
                </Avatar>
                <span
                  className={cn(
                    "flex-1 truncate text-sm",
                    isCurrent && "font-semibold"
                  )}
                >
                  {workspace.name}
                </span>
                {isCurrent ? (
                  <CheckIcon className="size-4 text-primary" />
                ) : null}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
